import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import isEmpty from 'lodash/isEmpty'

import {
  ActualTimeWindowInterval,
  ListQueryResponse,
  ScrollingParams,
  usePrevious,
  useSubscribeEvent,
} from '@hooks'
import { changeGlobalCheckBoxInColumnHeader, getFilterByQuickSearch } from '@helpers'
import { DIRTY_RULES, ENTITY, QuickRuleType, SEARCH_RULE_TYPE } from '@constants'
import {
  ALL_CHECKBOXES_IN_COLUMN_EVENT_DETAIL,
  CHECKBOX_IN_COLUMN_FOR_SEARCHING_EVENT_DETAIL,
  ConfiguredColumn,
  ObjectDataRecord,
  RESTRICTION_TYPE,
  RestrictionData,
  RestrictionDTO,
} from '@types'

import { EVENT_NAME, publish } from '../../../events'
import {
  getColumnFieldName,
  getColumnFieldNameForSort,
  getInitialColumnVisibilityModel,
  getIsAvailableRuleByType,
  getQuickSearchCheckedState,
  getSearchRuleForRequest,
  getSearchValueForRequest,
  getSortKeysForQuickSearchForRequest,
  getSortKeysOfColumnsForQuickSearch,
} from '../helpers'

type QuickSearchRefreshOverrides = {
  /** Значение для getFilterByQuickSearch (всегда строка). */
  searchValueForFilter?: string
  /** Значение для поля searchValue в объекте запроса (в boolean-режиме — boolean). */
  searchValueForPayload?: string | boolean | null
}

const buildQuickSearchRefreshParams = (
  entityType: ENTITY,
  searchValue: string,
  searchRuleName: string,
  sortKeysForQuickSearch: string[],
  scrollingParams?: Omit<ScrollingParams, 'page'>,
  overrides?: QuickSearchRefreshOverrides
): Omit<ScrollingParams, 'page'> & {
  sortKeysForQuickSearch: string[] | undefined
  searchValue: string | boolean | null | undefined
  searchRule: string | undefined
} => {
  const filterValue =
    overrides?.searchValueForFilter ??
    (getSearchValueForRequest(searchValue, searchRuleName) as string)
  const payloadSearchValue =
    overrides?.searchValueForPayload !== undefined
      ? overrides.searchValueForPayload
      : getSearchValueForRequest(searchValue, searchRuleName)

  const filter =
    entityType === ENTITY.DROP_DOWN_ENTITY || entityType === ENTITY.LIST_CONTROL
      ? getFilterByQuickSearch(
          getSearchRuleForRequest(searchValue, searchRuleName) as string,
          filterValue,
          getSortKeysForQuickSearchForRequest(
            sortKeysForQuickSearch,
            searchValue,
            searchRuleName
          ) as string[]
        )
      : undefined

  return {
    ...scrollingParams,
    filter,
    sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
      sortKeysForQuickSearch,
      searchValue,
      searchRuleName
    ),
    searchValue: payloadSearchValue,
    searchRule: getSearchRuleForRequest(searchValue, searchRuleName),
  }
}

type UseQuickSearchProps = {
  entityType: ENTITY
  searchRule: QuickRuleType
  sortKeysForQuickSearch: string[]
  valueSearchFilter: string
  viewColumns: ConfiguredColumn[]
  entityId: string | number | null
  dialogId?: string
  setValueSearchFilter: Dispatch<SetStateAction<string>>
  setSortKeysForQuickSearch: Dispatch<SetStateAction<string[]>>
  setSearchRule: Dispatch<SetStateAction<QuickRuleType>>
  refreshObjectData: (
    params?: Omit<ScrollingParams, 'page'> | undefined,
    skip?: boolean | undefined,
    timeWindow?: ActualTimeWindowInterval,
    filter?: string
  ) => Promise<ListQueryResponse<ObjectDataRecord>> | undefined
  handleUpdateRestrictions: (
    restrictions: RestrictionDTO[],
    localRestrictions?: RestrictionDTO[]
  ) => Promise<unknown>
  handleDeleteRestrictionsByType: (restrictionType: RESTRICTION_TYPE) => Promise<unknown>
  defaultEntityRestrictions?: RestrictionData
  defaultFilter?: string
}

export const useQuickSearch = ({
  entityType,
  searchRule,
  sortKeysForQuickSearch,
  valueSearchFilter,
  viewColumns,
  entityId,
  dialogId,
  setSearchRule,
  setValueSearchFilter,
  setSortKeysForQuickSearch,
  refreshObjectData,
  handleUpdateRestrictions,
  handleDeleteRestrictionsByType,
  defaultEntityRestrictions,
  defaultFilter,
}: UseQuickSearchProps) => {
  const [columnNameCheckBox, setColumnNameCheckBox] = useState<Record<string, boolean>>({})
  const [sortKeysColumnCheckBox, setSortKeysColumnCheckBox] = useState<Record<string, boolean>>({})
  // Состояние для выбора "режима" поиска: по умолчанию инпут с search полем типа string, или селект с
  // true/false в выпадающем списке для поиска boolean значений
  const [isBooleanSearchMode, setBooleanSearchMode] = useState(false)
  const [prevEntityId] = usePrevious(entityId)

  useEffect(() => {
    // для первоначальной инициализации строки быстрого поиска
    const isEmptyColumnNameCheckbox = isEmpty(columnNameCheckBox)
    const isEmptyColumnsSortKeysColumnCheckBox = isEmpty(sortKeysColumnCheckBox)

    if (isEmptyColumnNameCheckbox || prevEntityId !== entityId) {
      const initialColumnsNameCheckBox = getInitialColumnVisibilityModel(
        viewColumns,
        false,
        searchRule.name,
        defaultEntityRestrictions
      )

      Object.keys(initialColumnsNameCheckBox).forEach(columnName => {
        const column = viewColumns.find(
          col => getColumnFieldName(col) === columnName
        ) as ConfiguredColumn

        initialColumnsNameCheckBox[columnName] = column
          ? getQuickSearchCheckedState(
              column,
              searchRule.name,
              isBooleanSearchMode,
              defaultEntityRestrictions
            )
          : false
      })
      setColumnNameCheckBox(initialColumnsNameCheckBox)
    }

    if (isEmptyColumnsSortKeysColumnCheckBox || prevEntityId !== entityId) {
      const initialColumnsSortKey = getInitialColumnVisibilityModel(
        viewColumns,
        true,
        searchRule.name,
        defaultEntityRestrictions
      )

      Object.keys(initialColumnsSortKey).forEach(columnSortKey => {
        const column = viewColumns.find(
          col => getColumnFieldNameForSort(col) === columnSortKey
        ) as ConfiguredColumn

        initialColumnsSortKey[columnSortKey] = column
          ? getQuickSearchCheckedState(
              column,
              searchRule.name,
              isBooleanSearchMode,
              defaultEntityRestrictions
            )
          : false
      })

      if (entityType === ENTITY.DROP_DOWN_ENTITY) {
        const sortKeysOfColumnsForQuickSearch =
          getSortKeysOfColumnsForQuickSearch(initialColumnsSortKey)

        const sortKeysFilteredByAvailableSearchRules = sortKeysOfColumnsForQuickSearch.filter(
          sortKey => {
            const currentColumn = viewColumns.find(column => column.columnToSort === sortKey)

            if (!currentColumn) {
              return false
            }

            return getIsAvailableRuleByType(currentColumn, searchRule.name)
          }
        )

        setSortKeysForQuickSearch(sortKeysFilteredByAvailableSearchRules)

        return
      }

      setSortKeysColumnCheckBox(initialColumnsSortKey)
      setSortKeysForQuickSearch(getSortKeysOfColumnsForQuickSearch(initialColumnsSortKey))
    }
  }, [viewColumns, isBooleanSearchMode, searchRule])

  const handleSetBooleanMode = (value: boolean) => {
    setBooleanSearchMode(value)
  }

  const handleSetGlobalFilter = (value: string, skipRequest?: boolean) => {
    setValueSearchFilter(value)

    if (!isEmpty(sortKeysForQuickSearch)) {
      let booleanValue: string | boolean = ''

      if (isBooleanSearchMode) {
        if (value === 'true') {
          booleanValue = true
        }

        if (value === 'false') {
          booleanValue = false
        }
      }

      !skipRequest &&
        refreshObjectData(
          buildQuickSearchRefreshParams(
            entityType,
            value,
            searchRule.name,
            sortKeysForQuickSearch,
            undefined,
            isBooleanSearchMode
              ? {
                  searchValueForFilter: booleanValue.toString(),
                  searchValueForPayload: booleanValue,
                }
              : undefined
          )
        )
    }
  }

  const handleSetSortKeysForQuickSearch = (value: string[]) => setSortKeysForQuickSearch(value)
  const handleSetSortKeysColumnCheckBox = (value: Record<string, boolean>) =>
    setSortKeysColumnCheckBox(value)

  const handleSetColumnNameCheckBox = (value: Record<string, boolean>) =>
    setColumnNameCheckBox(value)

  const handleSubscibeCheckboxInColumnForSearching = useCallback(
    (data: { detail: CHECKBOX_IN_COLUMN_FOR_SEARCHING_EVENT_DETAIL }) => {
      const {
        searchValue,
        searchRule,
        sortKeys,
        titleColumns,
        value,
        sortKey,
        columnCode,
        columnName,
        scrollingParams,
        entityId: detailEntityId,
        dialogId: detailDialogId,
        wasSearchModeChanged,
        visibleColumns,
      } = data.detail

      if (detailEntityId !== entityId || detailDialogId !== dialogId) {
        return
      }

      const sortKeysCheckbox = { ...sortKeys }
      const columnNameCheckBox = { ...titleColumns }

      columnNameCheckBox[columnCode] = value

      handleUpdateRestrictions([
        {
          fieldName: columnName,
          fieldCode: columnCode,
          viewId: detailEntityId as string,
          restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
          value: value ? true : null,
        },
      ])

      // TODO: Выглядит как костыль, сделал, чтобы не тратить много часов на дефект
      // Обработка случая, чтобы две колонки с одинаковым sortKey не влияли друг на друга
      // Например, когда выбраны две колонки и одну отключаем, чтобы sortKeys не сбрасывался
      // и отправлялся дальше на бэк
      const isAlreadyUsedOtherColumn = Object.keys(columnNameCheckBox).some(key => {
        if (key === columnCode) {
          return false
        }

        const findColumn = visibleColumns.find(col => key === col.code)

        if (findColumn?.columnToSort === sortKey && columnNameCheckBox[key]) {
          return true
        }
      })

      if (!isAlreadyUsedOtherColumn) {
        sortKeysCheckbox[sortKey] = value
      }

      const newSortKeysForQuickSearch = getSortKeysOfColumnsForQuickSearch(sortKeysCheckbox)

      setColumnNameCheckBox(columnNameCheckBox)
      setSortKeysForQuickSearch(newSortKeysForQuickSearch)
      setSortKeysColumnCheckBox(sortKeysCheckbox)

      const isEmptySearchValue = !searchValue && DIRTY_RULES.includes(searchRule.name)

      if (!isEmpty(newSortKeysForQuickSearch) && (!isEmptySearchValue || wasSearchModeChanged)) {
        refreshObjectData(
          buildQuickSearchRefreshParams(
            entityType,
            searchValue,
            searchRule.name,
            newSortKeysForQuickSearch,
            scrollingParams
          ),
          false
        )
      }

      // Условие для случая, когда не выбрано ни одного чекбокса
      if (isEmpty(newSortKeysForQuickSearch)) {
        refreshObjectData(
          buildQuickSearchRefreshParams(
            entityType,
            searchValue,
            searchRule.name,
            newSortKeysForQuickSearch,
            scrollingParams
          ),
          false
        )
      }
    },
    [entityId, refreshObjectData, setSortKeysForQuickSearch]
  )

  const handleSetSearchRule = (
    { exception, name: newSearchRuleName }: QuickRuleType,
    allCheckCheckbox: boolean
  ) => {
    setSearchRule({ name: newSearchRuleName, exception })

    const emptyOrIsNotEmptyRule =
      newSearchRuleName === SEARCH_RULE_TYPE.EMPTY ||
      newSearchRuleName === SEARCH_RULE_TYPE.NOT_EMPTY

    if (exception) {
      let searchValue = valueSearchFilter

      const filteredColumnNameCheckBox = { ...columnNameCheckBox }
      const filteredSortKeysColumnCheckBox = { ...sortKeysColumnCheckBox }
      let hasFilteredColumns = false

      viewColumns.forEach(col => {
        const fieldName = getColumnFieldName(col)
        const sortKey = getColumnFieldNameForSort(col)

        if (filteredColumnNameCheckBox[fieldName] !== true) return

        if (!getIsAvailableRuleByType(col, newSearchRuleName)) {
          filteredColumnNameCheckBox[fieldName] = false
          if (sortKey && filteredSortKeysColumnCheckBox[sortKey] !== undefined) {
            filteredSortKeysColumnCheckBox[sortKey] = false
          }
          hasFilteredColumns = true
        }
      })

      const actualColumnNameCheckBox = hasFilteredColumns
        ? filteredColumnNameCheckBox
        : columnNameCheckBox
      const actualSortKeysColumnCheckBox = hasFilteredColumns
        ? filteredSortKeysColumnCheckBox
        : sortKeysColumnCheckBox

      if (hasFilteredColumns) {
        setColumnNameCheckBox(actualColumnNameCheckBox)
        setSortKeysColumnCheckBox(actualSortKeysColumnCheckBox)
      }

      let newSortKeysForQuickSearch = getSortKeysOfColumnsForQuickSearch(
        actualSortKeysColumnCheckBox
      )
      const isEmptySearchValue = !searchValue && DIRTY_RULES.includes(searchRule.name)

      if (allCheckCheckbox) {
        const { newColumnNameCheckbox, newSortKeysColumnCheckBox } =
          changeGlobalCheckBoxInColumnHeader(
            actualColumnNameCheckBox,
            actualSortKeysColumnCheckBox,
            true,
            setColumnNameCheckBox,
            setSortKeysColumnCheckBox,
            newSearchRuleName,
            viewColumns,
            isBooleanSearchMode
          )

        newSortKeysForQuickSearch = getSortKeysOfColumnsForQuickSearch(newSortKeysColumnCheckBox)

        publish(EVENT_NAME.GLOBAL_CHECKBOX_IN_COLUMN, {
          columnNameCheckbox: newColumnNameCheckbox,
          entityId,
        })
      }

      if (hasFilteredColumns || allCheckCheckbox) {
        setSortKeysForQuickSearch(newSortKeysForQuickSearch)
      }

      // если выбрано правило того же типа, что и предыдущее, то ничего не делаем
      if (
        searchRule.exception === exception &&
        isEmpty(newSortKeysForQuickSearch) &&
        isEmptySearchValue
      ) {
        return
      }

      // т.к. может не успеть обновиться setState
      // при новом exception сбрасываем строку поиска для EMPTY и NOT_EMPTY
      if (!DIRTY_RULES.includes(newSearchRuleName)) {
        searchValue = ''
        setValueSearchFilter(searchValue)
      }

      if (emptyOrIsNotEmptyRule || (!isEmpty(newSortKeysForQuickSearch) && isEmptySearchValue)) {
        refreshObjectData(
          buildQuickSearchRefreshParams(
            entityType,
            searchValue,
            newSearchRuleName,
            newSortKeysForQuickSearch
          ),
          false
        )
      }
    }
  }

  const handleSubscibeAllCheckboxesInColumn = useCallback(
    (data: { detail: ALL_CHECKBOXES_IN_COLUMN_EVENT_DETAIL }) => {
      const {
        searchRule,
        searchValue,
        sortKeys,
        scrollingParams,
        entityId: detailEntityId,
        dialogId: detailDialogId,
        columns,
        defaultFilter,
      } = data.detail

      if (detailEntityId !== entityId || detailDialogId !== dialogId) {
        return
      }

      const newSortKeysForQuickSearch = getSortKeysOfColumnsForQuickSearch(sortKeys)
      setSortKeysForQuickSearch(newSortKeysForQuickSearch)

      const isEmptySearchValue = !searchValue && DIRTY_RULES.includes(searchRule.name)

      if (!isEmpty(newSortKeysForQuickSearch)) {
        const restrictions: RestrictionDTO[] = []

        columns.forEach(column => {
          if (sortKeys[column.sortKey]) {
            restrictions.push({
              fieldName: column.name || column.code,
              fieldCode: column.code,
              viewId: detailEntityId as string,
              restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
              value: true,
            })
          }
        })

        handleUpdateRestrictions(restrictions)

        if (!isEmptySearchValue) {
          refreshObjectData(
            buildQuickSearchRefreshParams(
              entityType,
              searchValue,
              searchRule.name,
              newSortKeysForQuickSearch,
              scrollingParams
            )
          )
        }
      }

      // Условие для случая, когда не выбрано ни одного чекбокса
      if (isEmpty(newSortKeysForQuickSearch)) {
        handleDeleteRestrictionsByType(RESTRICTION_TYPE.QUICK_SEARCH_CHOICE).then(() =>
          refreshObjectData(
            buildQuickSearchRefreshParams(
              entityType,
              searchValue,
              searchRule.name,
              newSortKeysForQuickSearch,
              scrollingParams
            ),
            false,
            undefined,
            defaultFilter
          )
        )
      }
    },
    [entityId, refreshObjectData, setSortKeysForQuickSearch]
  )

  useSubscribeEvent(EVENT_NAME.ALL_CHECKBOXES_IN_COLUMN, handleSubscibeAllCheckboxesInColumn)

  useSubscribeEvent(
    EVENT_NAME.CHECKBOX_IN_COLUMN_FOR_SEARCHING,
    handleSubscibeCheckboxInColumnForSearching
  )

  return {
    state: {
      columnNameCheckBox,
      sortKeysColumnCheckBox,
      isBooleanSearchMode,
    },
    handlers: {
      handleSetGlobalFilter,
      handleSetSearchRule,
      handleSetSortKeysForQuickSearch,
      handleSetSortKeysColumnCheckBox,
      handleSetColumnNameCheckBox,
      handleSetBooleanMode,
    },
  }
}
